import { useState, useMemo, useTransition, useCallback, useEffect } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { CalendarIcon, X, Loader2, AlertCircle } from "lucide-react";
import { OutstandingInvoiceTable } from "@/components/par/OutstandingInvoiceTable";
import { NotificationSettingsModal } from "@/components/par/NotificationSettingsModal";
import { usePARReminders } from "@/hooks/usePARReminders";
import type { PARInvoiceRow, VendorSummary } from "@/lib/par/csvInvoiceParser";
import { buildVendorSummaries } from "@/lib/par/csvInvoiceParser";

export function StrategizeTab() {
  const {
    settings: reminderSettings,
    saving: reminderSaving,
    saveSettings: saveReminderSettings,
    persistInvoices,
  } = usePARReminders();

  const [allInvoiceRows, setAllInvoiceRows] = useState<PARInvoiceRow[]>([]);
  const [allVendors, setAllVendors] = useState<VendorSummary[]>([]);
  const [asOfDate, setAsOfDate] = useState<Date | undefined>(undefined);
  const [isDatePending, startDateTransition] = useTransition();
  const [showNotifications, setShowNotifications] = useState(false);
  const [isDemoLoading, setIsDemoLoading] = useState(true);

  const handleInvoicesParsed = useCallback(
    (rows: PARInvoiceRow[], vendorList: VendorSummary[]) => {
      setAllInvoiceRows(rows);
      setAllVendors(vendorList);
      persistInvoices(rows);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Auto-load PAR Systems demo data on mount
  useEffect(() => {
    fetch("/demo/par-invoice-data.json")
      .then((r) => r.json())
      .then((data: PARInvoiceRow[]) => {
        const vendorList = buildVendorSummaries(data);
        handleInvoicesParsed(data, vendorList);
      })
      .catch(console.error)
      .finally(() => setIsDemoLoading(false));
  }, [handleInvoicesParsed]);

  const handleAsOfDateChange = useCallback((date: Date | undefined) => {
    startDateTransition(() => setAsOfDate(date));
  }, []);

  const invoiceRows = useMemo(() => {
    if (!asOfDate) return allInvoiceRows;
    const cutoff = format(asOfDate, "yyyy-MM-dd");
    return allInvoiceRows.filter((r) => {
      if (!r.invoiced_date) return false;
      return r.invoiced_date <= cutoff;
    });
  }, [allInvoiceRows, asOfDate]);

  const vendors = useMemo(() => {
    if (!asOfDate) return allVendors;
    return buildVendorSummaries(invoiceRows);
  }, [invoiceRows, asOfDate, allVendors]);

  const referenceDate = asOfDate ? format(asOfDate, "yyyy-MM-dd") : undefined;

  const outstandingInvoices = useMemo(() => {
    return invoiceRows.filter((r) => {
      const paidBeforeCutoff = referenceDate
        ? r.paid_date && r.paid_date > referenceDate
          ? false
          : !!r.paid_date
        : !!r.paid_date || r.payment_status.toLowerCase().includes("paid");
      return !paidBeforeCutoff && r.amount_invoiced > r.amount_paid;
    });
  }, [invoiceRows, referenceDate]);

  // Loading demo data
  if (isDemoLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin text-primary" />
        Loading PAR Systems invoice data…
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* As-of date picker */}
      <div className="flex items-center gap-2">
        {asOfDate && (
          <Badge
            variant="outline"
            className="text-[10px] border-warning/40 text-warning bg-warning/5"
          >
            Viewing as of {format(asOfDate, "MMM d, yyyy")}
          </Badge>
        )}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn("h-8 text-xs gap-1.5", asOfDate && "border-warning/40 text-warning")}
            >
              <CalendarIcon className="w-3.5 h-3.5" />
              {asOfDate ? format(asOfDate, "MMM d, yyyy") : "As-of date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={asOfDate}
              onSelect={handleAsOfDateChange}
              disabled={(date) => date > new Date()}
              initialFocus
              className={cn("p-3 pointer-events-auto")}
            />
          </PopoverContent>
        </Popover>
        {asOfDate && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => handleAsOfDateChange(undefined)}
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        )}
        {isDatePending && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
            Updating…
          </div>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          {vendors.length} vendor{vendors.length !== 1 ? "s" : ""} ·{" "}
          {invoiceRows.length.toLocaleString()} invoice rows
        </span>
      </div>

      {/* Outstanding section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">Outstanding Invoices</h3>
        </div>
        <div className={cn(isDatePending && "opacity-50 pointer-events-none transition-opacity")}>
          <OutstandingInvoiceTable
            referenceDate={referenceDate}
            invoices={outstandingInvoices}
            onOpenNotifications={() => setShowNotifications(true)}
            msaTerms={msaTerms}
          />
        </div>
      </div>

      <NotificationSettingsModal
        open={showNotifications}
        onClose={() => setShowNotifications(false)}
        settings={reminderSettings}
        saving={reminderSaving}
        onSave={saveReminderSettings}
      />
    </div>
  );
}
